import React, { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import { getChartPalette, chartStateShell } from "./chartTheme";

type StackChartProps = {
  title: string;
  type: string;
  xLabel: string;
  yLabel: string;
  data: any;
  isFetching?: boolean;
  isDarkMode?: boolean;
};

export function StackChart({
  title,
  type,
  xLabel,
  yLabel,
  data,
  isFetching,
  isDarkMode = true,
}: StackChartProps) {
  const formattedData = useMemo(
    () => (typeof data === "object" && data !== null ? data : {}),
    [data]
  );

  const { labels, xAxisData, seriesData } = useMemo(() => {
    const slotWise = formattedData?.event_analysis || formattedData?.event_camera_wise || {};

    const categories: string[] = slotWise.categories || [];
    const seriesRaw: any[] = slotWise.series || [];

    // Check if categories array is empty or has length <= 0
    if (!categories || categories.length <= 0) {
      return { labels: [], xAxisData: [], seriesData: [] };
    }

    // Check if series array is empty or has length <= 0
    if (!seriesRaw || seriesRaw.length <= 0) {
      return { labels: [], xAxisData: [], seriesData: [] };
    }

    const labelSet = new Set<string>();
    const seriesMap: { [key: string]: number[] } = {};
    let xAxis: string[] = [];

    if (formattedData?.event_analysis) {
      xAxis = categories.map((cat) => {
        const dateObj = new Date(cat);
        if (Number.isNaN(dateObj.getTime())) {
          return String(cat);
        }
        const time = dateObj.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        });
        const date = dateObj.toLocaleDateString([], {
          day: "numeric",
          month: "numeric",
        });
        return `${time}\n${date}`;
      });
    } else if (formattedData?.event_camera_wise) {
      xAxis = categories.map((cat) => String(cat));
    }

    seriesRaw.forEach((s) => {
      labelSet.add(s.name);
      seriesMap[s.name] = s.data;
    });

    const finalSeries = Object.entries(seriesMap).map(([label, data]) => ({
      name: label,
      type: "bar",
      stack: "total",
      barWidth: formattedData?.event_camera_wise ? 40 : Math.max(20, Math.min(60, 700 / (xAxis.length || 1))),
      barGap: "20%",
      barCategoryGap: "30%",
      emphasis: { focus: "series" },
      data,
    }));

    return {
      labels: Array.from(labelSet),
      // 
      xAxisData: xAxis,
      seriesData: finalSeries,
    };
  }, [formattedData]);

  // Check if response is successful but categories array is empty
  const isNoDataFound = useMemo(() => {
    const slotWise = formattedData?.event_analysis || formattedData?.event_camera_wise || {};
    const categories: string[] = slotWise.categories || [];
    const series: any[] = slotWise.series || [];
    
    // If categories array length is 0 or less OR series array length is 0 or less, show "No data found"
    return categories.length <= 0 || series.length <= 0;
  }, [formattedData]);

  const option = useMemo(() => {
    const c = getChartPalette(isDarkMode);
    return {
      tooltip: {
        trigger: "axis",
        confine: true,
        appendToBody: true,
        axisPointer: { type: "shadow" },
        offset: [0, 20],
        formatter: function (params: any[]) {
          const time = params[0]?.axisValue?.replace("\n", " ");
          let tooltipHtml = `<strong>${time}</strong><br/>`;
          params.forEach((item) => {
            tooltipHtml += `${item.marker} ${item.seriesName}: <strong>${item.value}</strong><br/>`;
          });
          return tooltipHtml;
        },
      },
      legend: {
        type: "scroll",
        orient: "horizontal",
        bottom: "10%",
        data: labels,
        textStyle: {
          color: c.secondaryTextColor,
          fontSize: 12,
        },
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "22%",
        top: "15%",
        containLabel: true,
      },
      toolbox: {
        feature: {
          saveAsImage: { title: "Save", name: title },
        },
        iconStyle: {
          borderColor: c.borderColor,
        },
        emphasis: {
          iconStyle: {
            borderColor: c.emphasisIconBorder,
          },
        },
      },
      dataZoom: [
        {
          type: "slider",
          // show: true,
          xAxisIndex: 0,
          start: formattedData?.event_camera_wise ? 90 : 99,
          end: 100,
          bottom: 10,
          height: 12,
          showDetail: false,

          dataBackground: {
            lineStyle: { color: 'transparent' }, // line hide
            areaStyle: { color: 'transparent' }
          },
          // textStyle: { color: theme.secondaryTextColor },
          handleSize: 8,
          // handleStyle: { color: theme.handleColor },
          // fillerColor: theme.fillerColor,
          // borderColor: theme.axisLineColor,
          zoomOnMouseWheel: false,
          moveOnMouseWheel: true,
          moveOnMouseMove: true,
        },
        {
          type: "inside",
          xAxisIndex: 0,
          zoomOnMouseWheel: false,
          moveOnMouseWheel: true,
          moveOnMouseMove: true,
        },
      ],
      xAxis: {
        type: "category",
        name: xLabel,
        data: xAxisData,
        axisLabel: {
          rotate: 0,
          formatter: (value: string) => value,
          color: c.secondaryTextColor,
          fontSize: 10,
        },
        axisLine: { lineStyle: { color: c.axisLineColor } },
        axisTick: { lineStyle: { color: c.axisLineColor } },
      },
      yAxis: {
        type: "value",
        name: yLabel,
        nameTextStyle: { color: c.secondaryTextColor, fontSize: 12 },
        axisLabel: { color: c.secondaryTextColor, fontSize: 12 },
        axisLine: { lineStyle: { color: c.axisLineColor } },
        splitLine: {
          lineStyle: { color: c.splitLineColor, opacity: 0.5 },
        },
      },
      series: seriesData,
      color: c.colors,
    };
  }, [title, xLabel, yLabel, labels, xAxisData, seriesData, formattedData, isDarkMode]);

  // Show loading message when request is going to backend/response not coming yet
  if (isFetching) {
    return (
      <div className={chartStateShell}>
        <div className="px-4 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-slate-300 border-t-blue-500 dark:border-slate-500" />
          </div>
          <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
            Loading chart data...
          </p>
          <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
            Fetching stacked event details for this view.
          </p>
        </div>
      </div>
    );
  }

  // Show "no data" message when response comes successfully but categories/series are empty
  if (isNoDataFound) {
    return (
      <div className={chartStateShell}>
        <div className="px-4 text-center">
          <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
            No data found for this chart.
          </p>
          <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
            Change the filters or timeframe to see stacked events by category.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: '58vh', width: '100%' }}>
    <ReactECharts
      option={option}
      style={{ height: '98%', width: "100%" }}
      opts={{ renderer: "canvas" }}
      className="chart-container no-hover"
    />
    </div>
  );
}